import React, { useEffect, useRef, useState } from 'react';
import { scrollListToTopAfterPaint } from '../../../utils/scrollListToTop';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Project } from '../../../types/Project';
import { projectService, ProjectListResponse, ProjectFilters } from '../../../services/projectService';
import ProjectModal from '../components/ProjectModal';
import ProjectFiltersComponent from '../components/ProjectFilters';
import CancelProjectConfirmationModal from '../components/CancelProjectConfirmationModal';
import ServerPagination from '../../../components/ServerPagination';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { triggerBackendTest, triggerUnhandledCrash } from '../../../utils/bugsnagTest';
import { formatDateForDisplayUTC } from '../../../utils/dateUtils';
import InfoTooltip from '../../../components/InfoTooltip';
import { formatProjectStatus } from '../components/ProjectFilters';
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  PageHeader,
  RowActions,
  ACTIONS_COL_WIDTH,
  DataTable,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';

const SERVICE_AREAS = ['16', '17', '22', '25'];
const DEFAULT_PAGE_SIZE = 20;

function parseProjectsListParams(searchParams: URLSearchParams) {
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10);
  const searchFromUrl = searchParams.get('search') || '';
  const sortByUrl = searchParams.get('sortBy') || undefined;
  const sortOrderUrl =
    searchParams.get('sortOrder') === 'asc'
      ? 'asc'
      : searchParams.get('sortOrder') === 'desc'
        ? 'desc'
        : undefined;
  const filtersFromUrl: ProjectFilters = {
    serviceArea: searchParams.get('serviceArea') || '',
    status: searchParams.get('status') || '',
    type: searchParams.get('type') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    search: searchFromUrl,
    sortBy: sortByUrl,
    sortOrder: sortByUrl ? sortOrderUrl : undefined,
  };
  return { page, pageSize, filters: filtersFromUrl, searchFromUrl };
}

function buildProjectsListSearch(page: number, pageSize: number, filters: ProjectFilters): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });
  return params.toString();
}

const ProjectsView: React.FC = () => {
  // Helper function to safely format date for display
  const formatDateForDisplay = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return 'Not set';
    
    // Handle empty strings
    if (typeof dateValue === 'string' && dateValue.trim() === '') {
      return 'Not set';
    }
    
    // Project dates are stored at UTC midnight. Formatting them in local time renders the
    // previous day in every timezone behind UTC — i.e. everywhere this app is used — so
    // format in UTC via the shared helper.
    const formatted = formatDateForDisplayUTC(dateValue);
    return formatted === 'Unknown' ? 'Invalid Date' : formatted;
  };

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [isCancelProjectModalVisible, setIsCancelProjectModalVisible] = useState(false);
  const [projectToCancel, setProjectToCancel] = useState<Project | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [filters, setFilters] = useState<ProjectFilters>({
    serviceArea: '',
    status: '',
    startDate: '',
    endDate: '',
    type: '',
    search: ''
  });
  const [pendingFilters, setPendingFilters] = useState<ProjectFilters>({
    serviceArea: '',
    status: '',
    startDate: '',
    endDate: '',
    type: '',
    search: ''
  });
  const [filterOptions, setFilterOptions] = useState<{
    types: string[];
    statuses: string[];
    serviceAreas: { id: number; name: string }[];
  }>({
    types: [],
    statuses: [],
    serviceAreas: []
  });
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  const [projectTasks, setProjectTasks] = useState<Record<number, { tasks: any[]; loading: boolean; error: string | null; totalCount?: number; hasMore?: boolean }>>({});
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditProject = hasPermission('project:edit');
  const canCloseProject = hasPermission('project:close');
  const canCreateProject = hasPermission('project:create');
  const canCancelProject = hasPermission('project:cancel');
  const listScrollRef = useRef<HTMLDivElement>(null);
  const fetchRequestIdRef = useRef(0);
  const pendingScrollAfterCreateRef = useRef(false);

  // URL is the source of truth: sync UI state and fetch in one effect to avoid stale page-1 races.
  useEffect(() => {
    const { page, pageSize, filters: filtersFromUrl, searchFromUrl } = parseProjectsListParams(searchParams);

    setPagination(prev => ({ ...prev, page, pageSize }));
    setFilters(filtersFromUrl);
    setPendingFilters(filtersFromUrl);
    setSearchTerm(searchFromUrl);
    setSearchInput(searchFromUrl);

    const requestId = ++fetchRequestIdRef.current;

    const loadProjects = async () => {
      setLoading(true);
      try {
        const response: ProjectListResponse = await projectService.getProjectsPaginated(
          page,
          pageSize,
          filtersFromUrl
        );
        if (requestId !== fetchRequestIdRef.current) return;

        setProjects(response.data);
        setPagination(prev => ({
          ...prev,
          page,
          pageSize,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
          hasNext: response.pagination.hasNext,
          hasPrevious: response.pagination.hasPrevious
        }));
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) return;
        console.error('Error fetching projects:', error);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    loadProjects();
  }, [searchParams]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Add debugging for modal state changes
  useEffect(() => {
    console.log('ProjectsView: Modal state changed:', {
      isModalVisible,
      selectedProject: selectedProject ? {
        id: selectedProject.id,
        name: selectedProject.name,
        type: selectedProject.type
      } : null
    });
  }, [isModalVisible, selectedProject]);

  const fetchFilterOptions = async () => {
    try {
      const options = await projectService.getProjectFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const updateUrlParams = (newPagination?: Partial<typeof pagination>, newFilters?: ProjectFilters) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }
    
    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    
    setSearchParams(params);
  };

  const fetchProjects = async () => {
    const { page, pageSize, filters: filtersFromUrl } = parseProjectsListParams(searchParams);
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);
    try {
      const response: ProjectListResponse = await projectService.getProjectsPaginated(
        page,
        pageSize,
        filtersFromUrl
      );
      if (requestId !== fetchRequestIdRef.current) return;

      setProjects(response.data);
      setPagination(prev => ({
        ...prev,
        page,
        pageSize,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNext: response.pagination.hasNext,
        hasPrevious: response.pagination.hasPrevious
      }));

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(listScrollRef.current);
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Error fetching projects:', error);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAddProject = () => {
    setSelectedProject(undefined);
    setIsModalVisible(true);
  };

  const handleEditProject = (project: Project) => {
    console.log('ProjectsView: handleEditProject called with project:', {
      projectId: project.id,
      projectName: project.name,
      projectType: project.type,
      projectStatus: project.status
    });
    setSelectedProject(project);
    setIsModalVisible(true);
  };

  const handleSelectProject = (project: Project) => {
    const { page, pageSize, filters: currentFilters } = parseProjectsListParams(searchParams);
    navigate(`/projects/${project.id}`, {
      state: { projectsListSearch: buildProjectsListSearch(page, pageSize, currentFilters) }
    });
  };

  const toggleProjectTasks = async (projectId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (expandedProjectId === projectId) {
      // Collapse
      setExpandedProjectId(null);
    } else {
      // Expand - fetch tasks if not already loaded
      setExpandedProjectId(projectId);
      
      if (!projectTasks[projectId] || projectTasks[projectId].error) {
        // Set loading state
        setProjectTasks(prev => ({
          ...prev,
          [projectId]: { tasks: [], loading: true, error: null }
        }));
        
        try {
          // Fetch project with tasks - use smaller page size for faster loading
          // Only fetch first 50 tasks for summary view (can be paginated later if needed)
          const projectWithTasks = await projectService.getProjectById(projectId, 1, 50);
          setProjectTasks(prev => ({
            ...prev,
            [projectId]: { 
              tasks: projectWithTasks.tasks || [], 
              loading: false, 
              error: null,
              totalCount: projectWithTasks.taskPagination?.totalCount || (projectWithTasks.tasks?.length || 0),
              hasMore: (projectWithTasks.taskPagination?.totalCount || 0) > 50
            }
          }));
        } catch (error) {
          console.error('Error fetching project tasks:', error);
          setProjectTasks(prev => ({
            ...prev,
            [projectId]: { 
              tasks: [], 
              loading: false, 
              error: error instanceof Error ? error.message : 'Failed to load tasks' 
            }
          }));
        }
      }
    }
  };

  const handleSaveProject = async (projectData: Partial<Project>, h200FormFile?: File) => {
    try {
      const isNew = !selectedProject;
      let savedProject;
      
      if (selectedProject) {
        // Update existing project
        savedProject = await projectService.updateProject(selectedProject.id, projectData);
        
        // If there's an H200 form file to upload, handle it separately
        if (h200FormFile && projectData.type === 'H200') {
          try {
            await projectService.uploadH200Form(selectedProject.id, h200FormFile);
          } catch (uploadError) {
            console.error('Error uploading H200 form:', uploadError);
            // Continue with project update even if form upload fails
          }
        }
      } else {
        // Create new project
        savedProject = await projectService.createProject(projectData);
        
        // If there's an H200 form file to upload for a new project, handle it separately
        if (h200FormFile && projectData.type === 'H200' && savedProject.id) {
          try {
            await projectService.uploadH200Form(savedProject.id, h200FormFile);
          } catch (uploadError) {
            console.error('Error uploading H200 form:', uploadError);
            // Continue with project creation even if form upload fails
          }
        }
      }
      
      setIsModalVisible(false);
      if (isNew) {
        pendingScrollAfterCreateRef.current = true;
        const urlPage = parseInt(searchParams.get('page') || '1', 10);
        if (urlPage !== 1 || pagination.page !== 1) {
          updateUrlParams({ page: 1 }, filters);
        } else {
          await fetchProjects();
        }
      } else {
        await fetchProjects();
      }
    } catch (error) {
      console.error('Error saving project:', error);
      throw error;
    }
  };

  const handleCancelProject = async (reason: string) => {
    if (!projectToCancel) return;
    
    try {
      setLoading(true);
      
      await projectService.cancelProject(projectToCancel.id, reason);
      
      setIsCancelProjectModalVisible(false);
      setProjectToCancel(null);
      fetchProjects();
    } catch (error) {
      console.error('Error cancelling project:', error);
      alert('Error cancelling project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    const newPendingFilters = { ...pendingFilters, [name]: value };
    setPendingFilters(newPendingFilters);
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    updateUrlParams({ page: 1 }, pendingFilters); // Reset to page 1 when applying filters
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page }, filters);
  };

  // Server-side sort (allowlisted columns) persisted in the URL, like the filters.
  const currentSortBy = searchParams.get('sortBy');
  const currentSortOrder: 'asc' | 'desc' = searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc';
  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    updateUrlParams({ page: 1 }, { ...filters, sortBy: key, sortOrder: dir });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ pageSize, page: 1 }, filters);
  };

  // Search handlers (following routine tasks pattern)
  const toggleSearch = () => {
    setIsSearchVisible(prev => !prev);
    if (isSearchVisible) {
      setSearchInput(''); // Clear search when hiding
      setSearchTerm(''); // Clear search when hiding
    }
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    // Update URL params with trimmed search term
    const newFilters = { ...filters, search: trimmed };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };



  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    // Update URL params without search term
    const newFilters = { ...filters, search: '' };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: ProjectFilters = {
      serviceArea: '',
      status: '',
      startDate: '',
      endDate: '',
      type: '',
      search: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    setSearchInput('');
    setSearchTerm('');
    updateUrlParams({ page: 1 }, clearedFilters);
  };

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    return pendingFilters.serviceArea !== filters.serviceArea ||
           pendingFilters.status !== filters.status ||
           pendingFilters.startDate !== filters.startDate ||
           pendingFilters.endDate !== filters.endDate ||
           pendingFilters.type !== filters.type ||
           pendingFilters.search !== filters.search;
  };

  // The nested tasks list inside an expanded project row. Its own DataTable (rather than a
  // hand-rolled <table>) so it gets the same phone card layout instead of widening the outer
  // card on a narrow screen — a project row expanded on a phone would otherwise nest one
  // horizontal scroller inside another.
  const buildProjectTaskColumns = (project: Project): DataTableColumn<any>[] => [
    {
      key: 'taskNumber',
      header: 'Task Number',
      width: '15%',
      mobile: 'title',
      cell: (task) => (
        <span className="tw-inline-block tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-leading-none tw-text-white">
          {task.taskNumber || 'N/A'}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      width: '30%',
      mobile: 'subtitle',
      className: 'tw-break-words',
      cell: (task) => task.name || task.description || '-',
    },
    {
      key: 'status',
      header: 'Status',
      width: '15%',
      align: 'center',
      mobile: 'trailing',
      // Default (task) vocabulary — this is a task status, not a project status.
      cell: (task) => <StatusBadge status={task.status} />,
    },
    {
      key: 'type',
      header: 'Type',
      width: '15%',
      align: 'center',
      cell: (task) => (
        <span className={`tw-inline-block tw-whitespace-nowrap tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
          task.type === 'Quantified'
            ? 'tw-bg-[#cff4fc] tw-text-info tw-border-info'
            : task.type === 'Routine'
            ? 'tw-bg-warning tw-text-warning-text tw-border-[#ffc107]'
            : 'tw-bg-[#ced4da] tw-text-black tw-border-content'
        }`}>
          {task.type}
        </span>
      ),
    },
    {
      key: 'coding',
      header: (
        <InfoTooltip
          content={
            <div>
              <div><strong>Coding Formula:</strong></div>
              <div style={{ marginTop: '4px' }}>
                If a task belongs to a project that has a job number, the task is coded to the job number. Otherwise, the task is coded to its assigned activity number.
              </div>
              {projectTasks[project.id]?.tasks?.some((task: any) =>
                task.type === 'Custom' && !task.project?.jobNumber
              ) && (
                <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#ffc107' }}>
                  <strong>Note:</strong> For Custom tasks without job numbers, "ZZZ-ZZZZ" is used as a temporary coding number.
                </div>
              )}
            </div>
          }
          id={`coding-tooltip-projects-view-${project.id}`}
          placement="top"
          maxWidth="400px"
        >
          <span>
            Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }}></i>
          </span>
        </InfoTooltip>
      ),
      width: '15%',
      align: 'center',
      cell: (task) => {
        // If task has a project with job number, display job number; otherwise the activity number.
        if (task.project?.jobNumber) {
          return (
            <span className="tw-inline-block tw-rounded tw-border tw-border-primary tw-bg-[#cfe2ff] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-text-primary">
              {task.project.jobNumber}
            </span>
          );
        } else if (task.activities && task.activities.length > 0) {
          return (
            <span className="tw-inline-block tw-rounded tw-border tw-border-secondary tw-bg-[#e2e3e5] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-text-secondary">
              {task.activities[0].activityNumber}
            </span>
          );
        }
        return <span className="tw-text-content-muted tw-text-sm">-</span>;
      },
    },
    {
      key: 'activity',
      header: 'Activity',
      width: '15%',
      align: 'center',
      cell: (task) =>
        task.activities && task.activities.length > 0 ? (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {task.activities.map((activity: any, index: number) => (
              <span key={activity.id || index} className="tw-inline-block tw-rounded tw-border tw-border-secondary tw-bg-[#e2e3e5] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-text-secondary">
                {activity.activityNumber} - {activity.activityName}
              </span>
            ))}
          </div>
        ) : (
          <span className="tw-text-content-muted tw-text-sm">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '10%',
      align: 'right',
      mobile: 'trailing',
      cell: (task) => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectProject(project);
          }}
          title="View Task Details"
        >
          <i className="bi bi-eye"></i>
        </Button>
      ),
    },
  ];

  // Expanded-row content for a project: its tasks, with the same loading/error/"first 50"
  // affordances the inline table used to render directly in the <tr>.
  const renderProjectTasksExpanded = (project: Project) => {
    const state = projectTasks[project.id];
    return (
      <div className="tw-p-3" onClick={(e) => e.stopPropagation()}>
        {state?.loading ? (
          <div className="tw-text-center tw-py-3">
            <Spinner size="sm" className="tw-mr-2">Loading...</Spinner>
            Loading tasks...
          </div>
        ) : state?.error ? (
          <Alert variant="danger" className="tw-mb-0">
            <i className="bi bi-exclamation-triangle tw-mr-2"></i>
            {state.error}
          </Alert>
        ) : state?.tasks && state.tasks.length > 0 ? (
          <div>
            {state.hasMore && (
              <Alert variant="info" className="tw-mb-2 !tw-py-2">
                <i className="bi bi-info-circle tw-mr-2"></i>
                Showing first 50 tasks. Total: {state.totalCount} tasks.
              </Alert>
            )}
            <DataTable
              columns={buildProjectTaskColumns(project)}
              data={state.tasks}
              rowKey={(task) => task.id}
              size="sm"
              hover
              emptyMessage="No tasks found for this project"
            />
          </div>
        ) : (
          <div className="tw-text-center tw-text-content-muted tw-py-3">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
            No tasks found for this project
          </div>
        )}
      </div>
    );
  };

  // One column config drives both the desktop table and the phone stacked-card layout.
  // Project # is the card title, Name the secondary line under it, and Status + the row's
  // expander/kebab sit top-right as "trailing" (matching the Work IDs and Service Areas lists).
  const projectColumns: DataTableColumn<Project>[] = [
    {
      key: 'projectNumber',
      header: 'Project #',
      sortable: true,
      mobile: 'title',
      width: '10%',
      cell: (project) => (
        <span
          className="tw-inline-block tw-rounded tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-leading-none tw-text-primary"
          style={{ textDecoration: 'underline' }}
        >
          {project.projectNumber || 'N/A'}
        </span>
      ),
    },
    {
      key: 'jobNumber',
      header: 'Job #',
      sortable: true,
      width: '10%',
      cell: (project) =>
        project.jobNumber ? (
          <span>{project.jobNumber}</span>
        ) : project.type !== 'Internal' && project.type !== 'Quantified' && project.type !== 'Routine' ? (
          // Only show "Missing" if project type is NOT Internal, Quantified, or Routine
          <span className="tw-inline-block tw-rounded tw-border tw-border-danger tw-bg-[#f8d7da] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-text-danger">Missing</span>
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      mobile: 'subtitle',
      width: '25%',
      cell: (project) => project.name,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      width: '15%',
      cell: (project) => (
        // Type is not a status — its own colours (H200/Quantified/Routine/other), kept as-is.
        // tw-whitespace-nowrap added: this badge was clipping mid-word in a narrow column.
        <span className={`tw-inline-block tw-whitespace-nowrap tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
          project.type === 'H200'
            ? 'tw-bg-[#cfe2ff] tw-text-primary tw-border-primary'
            : project.type === 'Quantified'
            ? 'tw-bg-[#cff4fc] tw-text-info tw-border-info'
            : project.type === 'Routine'
            ? 'tw-bg-warning tw-text-warning-text tw-border-[#ffc107]'
            : 'tw-bg-[#ced4da] tw-text-black tw-border-content'
        }`}>
          {project.type}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      width: '15%',
      cell: (project) => (
        <StatusBadge status={project.status} vocabulary="project" label={formatProjectStatus(project.status)} soft />
      ),
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      width: '10%',
      cell: (project) => project.serviceArea?.id || 'N/A',
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      width: '10%',
      cell: (project) => formatDateForDisplay(project.startDate),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      width: ACTIONS_COL_WIDTH,
      cell: (project) => (
        // Expander + three-dot kebab — the Quantified Tasks layout.
        <RowActions
          expander={{
            expanded: expandedProjectId === project.id,
            onToggle: () => toggleProjectTasks(project.id),
            title: expandedProjectId === project.id ? 'Hide tasks' : 'Show tasks',
          }}
          actions={[
            {
              kind: 'edit',
              label: 'Edit Project',
              onClick: () => handleEditProject(project),
              hidden: !canEditProject,
            },
            {
              kind: 'cancel',
              label: 'Cancel Project',
              onClick: () => {
                setProjectToCancel(project);
                setIsCancelProjectModalVisible(true);
              },
              hidden: !(canCancelProject && project.status !== 'cancelled'),
            },
          ]}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div className="tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        <div className="tw-flex tw-justify-center tw-items-center tw-grow" style={{ minHeight: 0 }}>
          <Spinner>Loading...</Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      <PageHeader
        title="Projects"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              fetchProjects().finally(() => setLoading(false));
            },
          },
          {
            key: 'search',
            label: 'Search',
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible,
            title: isSearchVisible ? 'Close search' : 'Search projects',
            onClick: toggleSearch,
          },
          {
            key: 'export',
            label: 'Export',
            icon: 'bi-download',
            intent: 'neutral',
            onClick: () => console.log('Export projects'),
          },
          {
            key: 'add',
            label: 'Add Project',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateProject,
            onClick: handleAddProject,
          },
        ]}
      />

      <div className="tw-shrink-0">
        <ProjectFiltersComponent
          serviceAreas={SERVICE_AREAS}
          filters={pendingFilters}
          projects={projects}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          showApplyButton={true}
          onApply={handleApplyFilters}
          applyDisabled={!hasUnappliedChanges()}
          hasActiveFilters={hasActiveFilters()}
        />
      </div>

      {/* Search field appears below filters when visible */}
      {isSearchVisible && (
        <Row className="tw-mb-3 tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                size="sm"
                className="!tw-border-[#9ec5fe]"
                placeholder="Search by Project Name, Number, Description, Task Number, or Task Name..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => handleSearchKeyPress(e)}
                autoFocus
              />
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: "{searchTerm}"
              </div>
            )}
          </Col>
        </Row>
      )}

      <div ref={listScrollRef} className="tw-grow table-scroll-wrapper" style={{ minHeight: 0 }}>
        <DataTable
          columns={projectColumns}
          data={projects}
          rowKey={(project) => project.id}
          hover
          stickyHeader
          onRowClick={handleSelectProject}
          renderExpanded={renderProjectTasksExpanded}
          isExpanded={(project) => expandedProjectId === project.id}
          sortBy={currentSortBy}
          sortDir={currentSortOrder}
          onSort={handleSort}
          emptyMessage={
            <>
              <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
              No projects found
            </>
          }
        />
      </div>

      {/* Server-side pagination */}
      <div className="tw-shrink-0">
        <ServerPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          className="tw-mt-4"
        />
      </div>

      <ProjectModal
        visible={isModalVisible}
        onClose={() => {
          console.log('ProjectsView: Closing modal');
          setIsModalVisible(false);
        }}
        onSave={handleSaveProject}
        project={selectedProject}
      />

      <CancelProjectConfirmationModal
        visible={isCancelProjectModalVisible}
        onClose={() => {
          setIsCancelProjectModalVisible(false);
          setProjectToCancel(null);
        }}
        onConfirm={handleCancelProject}
        projectName={projectToCancel?.name}
      />
    </div>
  );
};

export default ProjectsView; 